import { createFileRoute } from '@tanstack/react-router'
import { AlternativesPage, alternativesHead, type AlternativesData } from '@/components/alternatives-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const data: AlternativesData = {
  tool: "Uptime Kuma",
  slug: "uptime-kuma",
  intro: "Uptime Kuma is a popular self-hosted monitor, but running your own server means your monitor can go down with your infrastructure. If you want hosted monitoring or another self-hosted option, here are alternatives.",
  alternatives: [
    {
      name: "Uptrack",
      description: "Hosted uptime monitoring with no server to run. Your monitor runs independently of your infrastructure, so it can detect outages even if your entire stack is down.",
      pricing: "Free (5 monitors), Pro $3/mo, Team $20/mo, Business $79/mo",
      pros: ["30-second checks on every plan", "Alerts only after 2-5 consecutive failed checks", "Flat pricing with a 30-day money-back guarantee"],
      cons: ["Free plan is 5 monitors at 30 s from one location", MONITOR_LOCATIONS.alternativesCon, "No APM or logs"],
      url: "https://uptrack.app",
      isUptrack: true,
    },
    {
      name: "Gatus",
      description: "Self-hosted health dashboard configured in YAML, with a Helm chart for Kubernetes.",
      pricing: "Free (Apache 2.0, self-hosted)",
      pros: ["Configuration as code in YAML", "Lightweight Go binary; Helm chart available", "Failure and success thresholds per endpoint"],
      cons: ["Requires self-hosting and maintenance", "No web UI for configuration"],
      url: "https://github.com/TwiN/gatus",
    },
    {
      name: "Statping-ng",
      description: "Self-hosted status page and monitor with themeable pages.",
      pricing: "Free (GPL-3.0, self-hosted)",
      pros: ["Built-in status pages with SASS themes", "HTTP, TCP, UDP, gRPC, and ICMP checks", "Choice of SQLite, PostgreSQL, or MySQL"],
      cons: ["Latest release is v0.93.0 (June 2025)", "Requires self-hosting infrastructure"],
      url: "https://github.com/statping-ng/statping-ng",
    },
    {
      name: "UptimeRobot",
      description: "A widely used uptime monitor with a free plan of 50 monitors at 5-minute checks, now allowed for commercial use. 30-second checks start on the Team plan.",
      pricing: "Free (50 monitors), Solo $10/mo, Team $41/mo",
      pros: ["Free plan with 50 monitors, commercial use allowed", "Mobile apps for iOS and Android", "15-second checks on the Scale plan"],
      cons: ["Free plan checks every 5 minutes; Solo every 60 seconds", "30-second checks start at $41/mo (Team)"],
      url: "https://uptimerobot.com",
    },
    {
      name: "HetrixTools",
      description: "Uptime, blacklist, and server monitoring, with a free plan of 15 monitors at 1-minute checks.",
      pricing: "Free (15 monitors), Professional $9.95/mo",
      pros: ["Free plan with 15 monitors at 1-minute checks", "Blacklist and agent-based server monitoring", "Unlimited status pages on all plans"],
      cons: ["1-minute minimum interval on all plans", "Professional plan covers 30 monitors"],
      url: "https://hetrixtools.com",
    },
  ],
  comparisonTable: [
    { tool: "Uptrack", freePlan: "5 monitors at 30s", checkInterval: "30s (all plans)", alertConfirmation: "Yes (2-5 consecutive)", startingPrice: "Free; Pro $3/mo" },
    { tool: "Gatus", freePlan: "Self-hosted", checkInterval: "Configurable", alertConfirmation: "Threshold-based", startingPrice: "Free" },
    { tool: "Statping-ng", freePlan: "Self-hosted", checkInterval: "Configurable", alertConfirmation: "Not documented", startingPrice: "Free" },
    { tool: "UptimeRobot", freePlan: "50 monitors", checkInterval: "5min (free), 30s (Team)", alertConfirmation: "Multi-location for timeouts", startingPrice: "$10/mo" },
    { tool: "HetrixTools", freePlan: "15 monitors", checkInterval: "1min", alertConfirmation: "Yes (multi-location)", startingPrice: "$9.95/mo" },
  ],
}

export const Route = createFileRoute('/_site/alternatives/uptime-kuma')({
  component: () => <AlternativesPage data={data} />,
  head: () => alternativesHead(data),
})
